import { parseISO } from 'date-fns'
import { MedicoesAtendimentosAnterioresQuery } from 'graphql/types.generated'
import { calcularIMC } from 'util/atendimento'
import { dateAsYyyyMmDd } from 'util/date/formatDate'
import { formatNumber, formatNumberAsPtBr, parseNumber } from 'util/number'
import { AntropometriaAccordionModel } from 'view/atendimentos/components/MedicoesPanel/AntropometriaAccordion'
import { SinaisVitaisAccordionModel } from 'view/atendimentos/components/MedicoesPanel/SinaisVitaisAccordion'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import {
  MedicaoAnteriorModel,
  MedicaoOutrosAtendimentosImcModel,
  MedicaoQueryModel,
  MedicoesRetificacaoQueryModel,
  UNIDADE_FORCA_MUSCULAR,
} from './model-medicoes'

export function convertMedicaoAtendimentoAtual(
  sinaisVitais: SinaisVitaisAccordionModel,
  antropometria: AntropometriaAccordionModel,
  dataAtendimento: Instant
): HistoricoMedicaoModel {
  return {
    dataMedicao: dataAtendimento,
    valorPeso: antropometria?.peso && parseNumber(antropometria.peso),
    valorAltura: antropometria?.altura && parseNumber(antropometria.altura),
    valorPerimetroCefalico: antropometria?.perimetroCefalico && parseNumber(antropometria.perimetroCefalico),
    valorCircunferenciaAbdominal:
      antropometria?.circunferenciaAbdominal && parseNumber(antropometria.circunferenciaAbdominal),
    perimetroPanturrilha: antropometria?.perimetroPanturrilha && parseNumber(antropometria.perimetroPanturrilha),
    valorImc: getIMCFromLocalizedNumbers(antropometria),
    tipoGlicemia: sinaisVitais?.tipoGlicemia,
    valorGlicemia: sinaisVitais?.glicemia && parseNumber(sinaisVitais.glicemia),
    valorFrequenciaCardiaca: sinaisVitais?.frequenciaCardiaca && parseNumber(sinaisVitais.frequenciaCardiaca),
    valorFrequenciaRespiratoria:
      sinaisVitais?.frequenciaRespiratoria && parseNumber(sinaisVitais.frequenciaRespiratoria),
    valorPressaoArterial:
      sinaisVitais?.pressaoArterialSistolica && sinaisVitais?.pressaoArterialDiastolica
        ? `${sinaisVitais.pressaoArterialSistolica}/${sinaisVitais.pressaoArterialDiastolica}`
        : undefined,
    valorSaturacaoO2: sinaisVitais?.saturacaoO2 && parseNumber(sinaisVitais.saturacaoO2),
    valorTemperatura: sinaisVitais?.temperatura && parseNumber(sinaisVitais.temperatura),
    origem: 'atendimentoAtual',
  }
}

export function convertMedicaoAnterior(medicaoAnterior: MedicaoAnteriorModel): HistoricoMedicaoModel {
  return {
    id: medicaoAnterior.id,
    dataMedicao: parseISO(medicaoAnterior.data),
    valorPeso: medicaoAnterior.peso && parseNumber(medicaoAnterior.peso),
    valorAltura: medicaoAnterior.altura && parseNumber(medicaoAnterior.altura),
    valorPerimetroCefalico: medicaoAnterior.perimetroCefalico && parseNumber(medicaoAnterior.perimetroCefalico),
    valorImc: getIMCFromLocalizedNumbers(medicaoAnterior),
    valorForcaMuscMaoDireita:
      medicaoAnterior.valorForcaMuscMaoDireita && formatNumber(medicaoAnterior.valorForcaMuscMaoDireita, 1),
    unidadeForcaMuscMaoDireita: medicaoAnterior.unidadeForcaMuscMaoDireita?.enum,
    valorForcaMuscMaoEsquerda:
      medicaoAnterior.valorForcaMuscMaoEsquerda && formatNumber(medicaoAnterior.valorForcaMuscMaoEsquerda, 1),
    unidadeForcaMuscMaoEsquerda: medicaoAnterior.unidadeForcaMuscMaoEsquerda?.enum,
    origem: 'medicoesAnteriores',
  }
}

export const convertMedicaoAnteriorRetificacao = (medicaoAnterior: MedicaoAnteriorModel): HistoricoMedicaoModel => {
  return {
    id: medicaoAnterior.id,
    dataMedicao: medicaoAnterior.data,
    valorPeso: medicaoAnterior.peso && parseNumber(medicaoAnterior.peso),
    valorAltura: medicaoAnterior.altura && parseNumber(medicaoAnterior.altura),
    valorPerimetroCefalico: medicaoAnterior.perimetroCefalico && parseNumber(medicaoAnterior.perimetroCefalico),
    valorImc: getIMCFromLocalizedNumbers(medicaoAnterior),
    origem: 'medicoesAnteriores',
  }
}

export function convertMedicaoOutrosAtendimentos(
  medicao: MedicoesAtendimentosAnterioresQuery['medicoes'][0]
): HistoricoMedicaoModel {
  return {
    ...medicao,
    valorAlturaUterina: Number(medicao.valorAlturaUterina),
    dataRegistro: medicao.atendimentoProfissional.iniciadoEm,
    nomeResponsavel: medicao.atendimentoProfissional.lotacao.profissional.nome,
    origem: 'outroAtendimento',
  }
}

export const convertMedicoesAnterioresQueryRetificacaoToForm = (
  medicoes: MedicoesRetificacaoQueryModel[]
): MedicaoAnteriorModel[] => {
  return medicoes
    .sort((a, b) => b.dataMedicao - a.dataMedicao)
    .map((medicao) => ({
      id: medicao.id,
      data: dateAsYyyyMmDd(medicao.dataMedicao),
      peso: medicao.valorPeso ? formatNumber(medicao.valorPeso, 3, false) : undefined,
      altura: medicao.valorAltura ? formatNumber(medicao.valorAltura, 1, false) : undefined,
      perimetroCefalico: medicao.valorPerimetroCefalico
        ? formatNumber(medicao.valorPerimetroCefalico, 1, false)
        : undefined,
      medicoes: {
        peso: medicao.valorPeso,
        altura: medicao.valorAltura,
        perimetroCefalico: medicao.valorPerimetroCefalico,
      },
    }))
}

export function convertMedicoesNoNascimento(
  antecedente: MedicoesAtendimentosAnterioresQuery['medicoesNoNascimento'],
  nascimentoCidadao: LocalDate
): HistoricoMedicaoModel {
  return {
    dataMedicao: nascimentoCidadao,
    valorPeso: antecedente.peso ? Number(antecedente.peso) : undefined,
    valorAltura: antecedente.altura ? Number(antecedente.altura) : undefined,
    valorPerimetroCefalico: antecedente.perimetroCefalico ? Number(antecedente.perimetroCefalico) : undefined,
    valorImc: getIMC(antecedente),
    origem: 'antecedente',
    dataRegistro: antecedente.dataUltimaAtualizacao,
  }
}

const getIMCFromLocalizedNumbers = ({ peso, altura }: { peso?: string; altura?: string }): number =>
  (peso && altura && calcularIMC(parseNumber(peso), parseNumber(altura))) || undefined

const getIMC = ({ peso, altura }: { peso?: string; altura?: string }): number =>
  (peso && altura && calcularIMC(Number(peso), Number(altura))) || undefined

export function convertMedicoesQueryToAntropometriaModel(medicao: MedicaoQueryModel): AntropometriaAccordionModel {
  return {
    altura: medicao?.valorAltura ? formatNumber(medicao.valorAltura, 1, false) : undefined,
    circunferenciaAbdominal: medicao?.valorCircunferenciaAbdominal
      ? formatNumber(medicao.valorCircunferenciaAbdominal, 1, false)
      : undefined,
    perimetroCefalico: medicao?.valorPerimetroCefalico
      ? formatNumber(medicao.valorPerimetroCefalico, 1, false)
      : undefined,
    perimetroPanturrilha: medicao?.perimetroPanturrilha
      ? formatNumber(medicao.perimetroPanturrilha, 1, false)
      : undefined,
    peso: medicao?.valorPeso ? formatNumber(medicao.valorPeso, 3, false) : undefined,
    membroInferiorDireito: medicao?.membroInferiorDireito,
    membroInferiorEsquerdo: medicao?.membroInferiorEsquerdo,
    pele: medicao?.pele,
    olhoDireito: medicao?.olhoDireito,
    olhoEsquerdo: medicao?.olhoEsquerdo,
    ouvidoDireito: medicao?.ouvidoDireito,
    ouvidoEsquerdo: medicao?.ouvidoEsquerdo,
    valorForcaMuscMaoDireita: medicao?.valorForcaMuscMaoDireita
      ? formatNumberAsPtBr(formatNumber(Number(medicao.valorForcaMuscMaoDireita), 1))
      : undefined,
    unidadeForcaMuscMaoDireita: medicao?.unidadeForcaMuscMaoDireita
      ? {
          enum: medicao.unidadeForcaMuscMaoDireita,
          nome: UNIDADE_FORCA_MUSCULAR[medicao.unidadeForcaMuscMaoDireita].nome,
          sigla: UNIDADE_FORCA_MUSCULAR[medicao.unidadeForcaMuscMaoDireita].sigla,
        }
      : undefined,
    valorForcaMuscMaoEsquerda: medicao?.valorForcaMuscMaoEsquerda
      ? formatNumberAsPtBr(formatNumber(Number(medicao.valorForcaMuscMaoEsquerda), 1))
      : undefined,
    unidadeForcaMuscMaoEsquerda: medicao?.unidadeForcaMuscMaoEsquerda
      ? {
          enum: medicao.unidadeForcaMuscMaoEsquerda,
          nome: UNIDADE_FORCA_MUSCULAR[medicao.unidadeForcaMuscMaoEsquerda].nome,
          sigla: UNIDADE_FORCA_MUSCULAR[medicao.unidadeForcaMuscMaoEsquerda].sigla,
        }
      : undefined,
  }
}

export function convertMedicaoOutrosAtendimentosImc(medicao: MedicaoOutrosAtendimentosImcModel): HistoricoMedicaoModel {
  return {
    id: medicao.id,
    dataMedicao: medicao.dataMedicao,
    valorPeso: medicao.valorPeso ?? undefined,
    valorAltura: medicao.valorAltura ?? undefined,
    origem: 'outroAtendimento',
  }
}
